import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useQuery as useApolloQuery } from '@apollo/client';
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  Download,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { GET_DAILY_DELIVERY_REPORT } from '../Graphql/Queries';
import { generateMockData } from '../Graphql/MockData';
import * as XLSX from 'xlsx';
import logger from '@/utils/logger';

// GraphQL interfaces matching the API response
interface DeliveryReportItem {
  id: string;
  deliveryGoal: number;
  creativeNames: string[];
  creativeTypes: string[];
  dealId: string;
  playerName: string;
  publisherName: string;
  firstPlayDate: string;
  lastPlayDate: string;
  creativeDuration?: number;
  adPlays?: number;
  date?: string;
  startTime?: string;
  endTime?: string;
  userAgent?: string;
  timezone?: string;
  country?: string;
  city?: string;
  ipAddress?: string;
}

interface DeliveryReport {
  adPlays: number;
  deliveryGoal: number;
  inventoryCount: number;
  reports: DeliveryReportItem[];
}

// Table columns matching Promotional
const tableColumns = [
  { id: 'InventoryName', label: 'INVENTORY NAME', sortable: false },
  { id: 'ReferenceId', label: 'REFERENCE ID', sortable: false },
  { id: 'Date', label: 'DATE', sortable: false },
  { id: 'CreativeName', label: 'CREATIVE NAME', sortable: false },
  { id: 'CreativeType', label: 'CREATIVE TYPE', sortable: false },
  { id: 'AdPlay', label: 'ADPLAY', sortable: false },
  { id: 'LastModified', label: 'LAST MODIFIED', sortable: false },
  { id: 'Actions', label: 'ACTION', sortable: false, isAction: true },
];

interface ProofOfLogsProps {
  base_path?: string;
}

export default function ProofOfLogs({ base_path = '' }: ProofOfLogsProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign') || '1';
  const billboardId = searchParams.get('billboard') || '1';
  const dealId = searchParams.get('dealId') || 'E-2023-061200000007'; // Use dealId from URL or fallback
  const billboardNameFromUrl = searchParams.get('billboardName') || ''; // Get billboard name from URL

  const [currentPage, setCurrentPage] = useState(1);
  const [deliveryReport, setDeliveryReport] = useState<DeliveryReport | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billboardName, setBillboardName] = useState<string>(billboardNameFromUrl || 'Billboard');
  const reportsPerPage = 10;

  // Authentication and environment variables (matching other pages approach)
  const token = localStorage.getItem('authToken');

  logger.log('Proof of Logs Debug:', {
    campaignId,
    billboardId,
    dealId, // Add dealId to debug output
    billboardName: billboardNameFromUrl,
    actualBillboardNameUsed: billboardName,
    token: token ? 'exists' : 'missing',
    mockDataEnabled: import.meta.env.VITE_APP_MOCK_DATA === 'true'
  });

  // We need campaign data to get dealId for the API call
  // Get dealId from URL parameters (passed from delivery report) or use fallback
  const mockCampaign = {
    id: campaignId,
    dealId: dealId, // Use the dealId from URL parameter
    name: "OnePlus Nord 5G Campaign"
  };

  const mockInventory = {
    billboardId: billboardId,
    billboardName: "Digital Billboard - Times Square",
    startDate: "2023-07-01",
    endDate: "2023-07-31"
  };

  // Apollo GraphQL query for real data
  const { data: apolloData, loading: apolloLoading, error: apolloError, refetch } = useApolloQuery(GET_DAILY_DELIVERY_REPORT, {
    variables: {
      accessToken: token,
      dealId: mockCampaign.dealId,
      inventoryId: billboardId,
      startDate: mockInventory.startDate,
      endDate: new Date().toISOString().split('T')[0], // Today's date
    },
    skip: !token || !campaignId || !billboardId || import.meta.env.VITE_APP_MOCK_DATA === 'true',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    fetchPolicy: "network-only",
  });

  // Fallback to mock data for development (only when explicitly enabled)
  const { data: mockDeliveryData, isLoading: mockLoading, error: mockError } = useQuery({
    queryKey: ['/api/daily-delivery-report', campaignId, billboardId, currentPage],
    queryFn: async () => {
      // Only use mock data when explicitly enabled
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockResponse = await generateMockData(GET_DAILY_DELIVERY_REPORT);
        return mockResponse.data.campaignDeliveryReport || null;
      }

      // Return null if mock data is not enabled
      return null;
    },
    enabled: import.meta.env.VITE_APP_MOCK_DATA === 'true' && !!campaignId && !!billboardId
  });

  // Data processing effects
  useEffect(() => {
    const includeMockLoading = import.meta.env.VITE_APP_MOCK_DATA === 'true' ? mockLoading : false;
    setIsLoading(apolloLoading || includeMockLoading);
    setError(apolloError ? apolloError.message : (mockError ? (mockError as Error).message : null));
  }, [apolloLoading, mockLoading, apolloError, mockError]);

  // Process delivery report data when Apollo data changes or mock data is available
  useEffect(() => {
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      // Use mock data
      if (mockDeliveryData) {
        const reports = mockDeliveryData.reports || [];
        setDeliveryReport(mockDeliveryData);
        setTotalPages(Math.ceil(reports.length / reportsPerPage));
        // Keep the billboard name from URL parameter if available
        if (!billboardNameFromUrl) {
          setBillboardName(mockInventory.billboardName);
        }
      }
    } else {
      // Use real GraphQL data
      if (apolloData?.campaignDeliveryReport) {
        const reportData = apolloData.campaignDeliveryReport;
        const reports = reportData.reports || [];
        setDeliveryReport(reportData);
        setTotalPages(Math.ceil(reports.length / reportsPerPage));
        // Keep the billboard name from URL parameter if available
        // Don't override with mock data when using real API
      }
    }
  }, [apolloData, mockDeliveryData, reportsPerPage, billboardNameFromUrl]);

  // Helper functions for date formatting
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC' // prevents shift to local timezone
    });
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Navigation handler for detailed report
  const onClickViewDetailedReport = (report: DeliveryReportItem) => {
    // Navigate to detailed delivery report page with report data
    const reportDate = new Date(report.firstPlayDate).toISOString().split('T')[0];
    navigate(`${base_path}/daily-report?campaign=${campaignId}&billboard=${billboardId}&reportId=${report.id}&dealId=${dealId}&date=${reportDate}&billboardName=${encodeURIComponent(billboardName)}`);
  };

  const handleDownloadTemplate = () => {
    if (!deliveryReport || !deliveryReport.reports || deliveryReport.reports.length === 0) {
      logger.log('No data to download');
      return;
    }

    // Prepare data for Excel
    const excelData = deliveryReport.reports.map((report) => ({
      'Inventory Name': billboardName,
      'Reference ID': report.dealId,
      'Network Reference ID': ' ', // Empty as per sample
      'Month': new Date(report.firstPlayDate).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit' 
      }).replace(/\//g, '-'),
      'Delivery Goal': deliveryReport.deliveryGoal || 'N/A',
      'Ad Plays': report.adPlays || 0,
      'Last Modified Date': new Date(report.lastPlayDate).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }),
      'Publisher': report.publisherName || 'N/A',
      'Creative Types': `[${report.creativeTypes.join(', ')}]`,
      'Creatives': `[${report.creativeNames.join(', ')}]`,
      'Player': report.playerName || 'N/A'
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Playlog Report');

    // Generate filename with current date
    const fileName = `Playlog_Report_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Download file
    XLSX.writeFile(wb, fileName);
  };

  // Get current page items
  const reports = deliveryReport?.reports || [];
  const startIndex = (currentPage - 1) * reportsPerPage;
  const endIndex = startIndex + reportsPerPage;
  const currentItems = reports.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[95vw] mx-auto px-4 py-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Loading Daily Playlog...</h1>
            <p className="text-gray-600 mt-2">Please wait while we fetch your playlog data.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[95vw] mx-auto px-4 py-6">
          <div className="mb-6">
            <Link to={`${base_path}/delivery-report?campaign=${campaignId}&billboardName=${encodeURIComponent(billboardNameFromUrl || '')}`}>
              <Button variant="ghost" size="sm" className="hover:bg-emerald-50">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Error Loading Daily Playlog</h1>
            <p className="text-gray-600 mt-2">{error}</p>
            <div className="mt-4 space-x-2">
              <Link to={`${base_path}/delivery-report?campaign=${campaignId}&billboardName=${encodeURIComponent(billboardNameFromUrl || '')}`}>
                <Button variant="outline">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Delivery Report
                </Button>
              </Link>
              <Button
                onClick={() => {
                  if (import.meta.env.VITE_APP_MOCK_DATA !== 'true' && refetch) {
                    refetch();
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-[95vw] mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4 mt-3">
            <div>
              <h1 className="text-2xl font-bold text-emerald-600">Daily Playlog</h1>
              <p className="text-gray-600">Your Playlog History</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleDownloadTemplate}
              variant="outline"
              size="sm"
              className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`${base_path}/delivery-report?campaign=${campaignId}&billboardName=${encodeURIComponent(billboardName)}`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        </div>

        {/* Summary Info */}
        <div className="mb-6">
          <p className="text-gray-600">
            {reports.length || 0} spots in this date/time range found
          </p>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {tableColumns.map((col) => (
                      <th
                        key={col.id}
                        className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${col.id === 'InventoryName' ? 'text-left' : 'text-center'
                          }`}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.length > 0 ? (
                    currentItems.map((report: DeliveryReportItem, index) => (
                      <tr key={report.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {billboardName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                          {report.dealId}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                          {formatDate(report.firstPlayDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                          {report.creativeNames.join('/')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                          {report.creativeTypes.join('/')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                          {report.adPlays || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                          {formatDate(report.lastPlayDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                          <Button
                            onClick={() => onClickViewDetailedReport(report)}
                            variant="ghost"
                            size="sm"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Detailed Playlog
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={tableColumns.length} className="text-center py-8 text-gray-500">
                        No playlog entries found for this date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4 p-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1 rotate-90" />
                  Previous
                </Button>

                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronLeft className="h-4 w-4 ml-1 -rotate-90" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
